/**
 * Subscribe to live messenger deltas for a set of conversation ids.
 *
 *   const ids = useMemo(() => visibleConversations.map(c => c.id), [...])
 *   useMessengerSubscription(ids)
 *
 * Behavior:
 *   - Joins one `conversation:<id>` Phoenix channel per id.
 *   - Reuses channels across renders; only adds/removes when the set
 *     membership changes (set-diffed via a sorted, deduped key string —
 *     mirrors `usePresenceSubscription`).
 *   - Wires every channel event to the Zustand store dispatchers so the
 *     UI re-renders without props plumbing.
 *   - Tears down all channels on unmount.
 *
 * No-op until auth is hydrated.
 */

import { useEffect, useRef } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import {
  joinConversationChannel,
  markDeliveredUpTo,
  type ConversationChannelHandle,
} from '../services/messenger.socket'
import { useMessengerStore } from '../store/messenger.store'
import { messengerApi } from '../services/messenger.api'
import { publishIncomingMessage } from '../services/incoming-message-bus'

/** Shape of a `system` message's JSON body. Mirrors `buildSystemText`'s
 *  parse in conversation-panel.tsx, the other consumer of this wire shape. */
interface SystemEventBody {
  event?: string
  actorId?: string
  targetIds?: string[]
}

function parseSystemEvent(body: string | null): SystemEventBody | null {
  if (!body) return null
  try {
    return JSON.parse(body) as SystemEventBody
  } catch {
    return null
  }
}

/** Does this system message body describe a change to who is in the group? */
function membershipChanged(parsed: SystemEventBody | null): boolean {
  return parsed?.event === 'added' || parsed?.event === 'removed' || parsed?.event === 'left'
}

/**
 * Re-read one conversation so its participant list is current.
 *
 * Participants are preserved rather than trusted blindly: the detail endpoint
 * is the only one that hydrates them, and a response without them would
 * otherwise blank the roster it was fetched to correct.
 */
async function refreshParticipants(conversationId: string): Promise<void> {
  try {
    const { conversation } = await messengerApi.getConversation(conversationId)
    const store = useMessengerStore.getState()
    const existing = store.wireConversations.get(conversationId)
    store.upsertWireConversation({
      ...conversation,
      participants: conversation.participants ?? existing?.participants,
    })
  } catch {
    // A failed refresh leaves the roster as it was; the next open corrects it.
  }
}

export function useMessengerSubscription(conversationIds: string[]): void {
  const me = useAuthStore((s) => s.user)
  // Chat "is me" identity = contactPresenceId (message.senderId + reaction/typing
  // userIds are keyed on it). The socket connection itself still authenticates
  // as the auth user (`me.id`) — that's the token `sub` meetings use — so socket
  // calls below keep passing `me.id`, while equality checks use `presenceId`.
  const presenceId = useAuthStore((s) => s.presenceId)
  const handlesRef = useRef<Map<string, ConversationChannelHandle>>(new Map())

  // Stable, deduped, sorted comma string — equal arrays of ids produce the
  // same key, preventing churn on identity-different-but-value-equal arrays.
  const key = Array.from(new Set(conversationIds)).filter(Boolean).sort().join(',')

  useEffect(() => {
    // No presence identity yet (not consented / still resolving) → don't start
    // chat subscriptions. They resume once `presenceId` lands.
    if (!me?.id || !presenceId) return
    let cancelled = false

    const targetIds = key ? key.split(',') : []
    const current = handlesRef.current

    // Leave channels no longer in the desired set.
    for (const [id, handle] of current.entries()) {
      if (!targetIds.includes(id)) {
        try { handle.leave() } catch { /* ignore */ }
        current.delete(id)
      }
    }

    // Join channels newly in the desired set.
    ;(async () => {
      const socketUserId = me.id       // socket/token identity (meetings auth user)
      const myId = presenceId          // chat "is me" identity (contactPresenceId)
      const myName = me.name

      for (const conversationId of targetIds) {
        if (cancelled) break
        if (current.has(conversationId)) continue

        try {
          const handle = await joinConversationChannel(
            socketUserId,
            conversationId,
            {
              onMessageNew: (message) => {
                useMessengerStore.getState().appendWireMessage(message)
                // Fan out to anything that wants to react to a LIVE arrival
                // (not a history load, edit, or reaction), currently just
                // the in-app alert hook, which is mounted outside this
                // conversation's own UI and so has no other way to see it.
                publishIncomingMessage(message)
                if (message.type === 'system') {
                  const parsed = parseSystemEvent(message.body)
                  // A membership change arrives as a system message and
                  // nothing else: there is no participant event on the
                  // wire. Without this the roster and the "N members" count
                  // stayed at whatever they were when the conversation was
                  // last fetched, so someone arriving on an invite link
                  // showed up as "X joined" in the thread while the header
                  // above it still read the old number until a reload.
                  if (membershipChanged(parsed)) {
                    void refreshParticipants(conversationId)
                  }
                  // I was the one removed, or I left this same group from
                  // another device: the open thread has to stop looking
                  // usable right away rather than wait on the participant
                  // refresh above to notice I'm gone. Reversed if I'm added
                  // back (`conversation:removed`, see messenger.socket.ts, is
                  // the reliable signal for the removed member themselves;
                  // this covers the case where the system message still
                  // reaches an open tab before the channel closes).
                  if (parsed?.event === 'removed' && parsed.targetIds?.includes(myId)) {
                    useMessengerStore.getState().markRemovedFrom(conversationId)
                  } else if (parsed?.event === 'left' && parsed.actorId === myId) {
                    useMessengerStore.getState().markRemovedFrom(conversationId)
                  } else if (parsed?.event === 'added' && parsed.targetIds?.includes(myId)) {
                    useMessengerStore.getState().clearRemovedFrom(conversationId)
                  }
                }
                // I received a message from someone else → advance my delivered
                // high-water-mark so the sender sees ✓✓ (grey).
                if (message.senderId && message.senderId !== myId) {
                  markDeliveredUpTo(socketUserId, conversationId, message.createdAt, myName)
                }
              },
              onMessageUpdated: (message) => {
                useMessengerStore.getState().replaceWireMessage(message)
              },
              onMessageDeleted: ({ conversationId: convId, messageId }) => {
                useMessengerStore.getState().markWireMessageDeleted(convId, messageId)
              },
              onReactionAdded: ({ messageId, userId, emoji }) => {
                useMessengerStore.getState().applyReactionAdded(
                  conversationId, messageId, userId, emoji,
                  userId === myId,
                )
              },
              onReactionRemoved: ({ messageId, userId, emoji }) => {
                useMessengerStore.getState().applyReactionRemoved(
                  conversationId, messageId, userId, emoji,
                  userId === myId,
                )
              },
              onReadUpdated: ({ conversationId: convId, userId, lastReadAt }) => {
                useMessengerStore.getState().applyReadUpdate(convId, userId, lastReadAt)
              },
              onDeliveredUpdated: ({ conversationId: convId, userId, lastDeliveredAt }) => {
                useMessengerStore.getState().applyDeliveredUpdate(convId, userId, lastDeliveredAt)
              },
              onTyping: ({ conversationId: convId, userId }) => {
                // Don't display "you are typing" for the local user.
                if (userId === myId) return
                useMessengerStore.getState().markTyping(convId, userId)
              },
              onConversationRemoved: () => {
                // The dedicated, reliable signal, fired on MY channel only
                // right before the server closes it with a clean phx_close
                // (no auto-rejoin; see messenger.socket.ts). Drop our handle
                // too, so a later run of this effect doesn't try to leave an
                // already-dead channel or skip rejoining if this id is ever
                // reused.
                useMessengerStore.getState().markRemovedFrom(conversationId)
                current.delete(conversationId)
              },
            },
            myName,
          )

          if (cancelled) {
            try { handle.leave() } catch { /* ignore */ }
            continue
          }
          current.set(conversationId, handle)

          // Mark delivered up to this conversation's latest message on join — so
          // a sender sees ✓✓ (grey) as soon as I come online / open the app, even
          // for messages that arrived while I was OFFLINE (those don't re-fire
          // onMessageNew). markDeliveredUpTo is monotonic + deduped per
          // conversation, so it only pushes when there's genuinely a newer message.
          const lastAt = useMessengerStore.getState().wireConversations.get(conversationId)?.lastMessageAt
          if (lastAt) markDeliveredUpTo(socketUserId, conversationId, lastAt, myName)
        } catch {
          // Forbidden (ACL deny) or transient — leave room for retry on remount.
          // UI shows the conversation in offline-history mode.
        }
      }
    })().catch(() => { /* socket teardown / token miss — bail silently */ })

    return () => { cancelled = true }
  }, [key, me?.id, me?.name, presenceId])

  // On full unmount of the consumer, drop every channel we own.
  useEffect(() => {
    const owned = handlesRef.current
    return () => {
      for (const handle of owned.values()) {
        try { handle.leave() } catch { /* ignore */ }
      }
      owned.clear()
    }
  }, [])
}
